import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { cn } from "../util";

type Aviso = {
  id: number;
  titulo: string;
  descripcion?: string;
  tono?: "neutro" | "exito" | "error";
};
type Contexto = { avisar: (a: Omit<Aviso, "id">) => void };

const ToastContexto = React.createContext<Contexto | null>(null);

/** Envuelve la app una vez; después `const { avisar } = useToast()`. */
export function ToastProveedor({ children }: { children: React.ReactNode }) {
  const [avisos, setAvisos] = React.useState<Aviso[]>([]);
  const avisar = React.useCallback((a: Omit<Aviso, "id">) => {
    setAvisos((prev) => [...prev, { ...a, id: Date.now() + Math.random() }]);
  }, []);
  const valor = React.useMemo(() => ({ avisar }), [avisar]);
  return (
    <ToastContexto.Provider value={valor}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {avisos.map((a) => (
          <ToastPrimitive.Root
            key={a.id}
            onOpenChange={(abierto) =>
              !abierto && setAvisos((prev) => prev.filter((x) => x.id !== a.id))
            }
            className={cn(
              "group relative flex w-full items-start gap-3 border bg-fondo p-4 font-ui shadow-md",
              a.tono === "error"
                ? "border-rojo"
                : a.tono === "exito"
                  ? "border-verde"
                  : "border-linea-fuerte",
            )}
          >
            <div className="grid gap-0.5">
              <ToastPrimitive.Title className="text-sm font-semibold text-tinta">
                {a.titulo}
              </ToastPrimitive.Title>
              {a.descripcion && (
                <ToastPrimitive.Description className="text-sm text-tinta-tenue">
                  {a.descripcion}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close
              className="ml-auto opacity-60 hover:opacity-100"
              aria-label="Cerrar"
            >
              <X className="size-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4" />
      </ToastPrimitive.Provider>
    </ToastContexto.Provider>
  );
}

export function useToast(): Contexto {
  const ctx = React.useContext(ToastContexto);
  if (!ctx) throw new Error("useToast necesita <ToastProveedor> arriba en el árbol.");
  return ctx;
}
